import _ from 'lodash'
import Axios from 'axios'
import Store from '@/vuex'
import { serialized_vuex_config_object as vuex_global_state_config_object } from '@/vuex/config'
import { serialized_vuex_config_object as vuex_module_state_config_object } from '@running_app_src/store'
import { log } from '@utilities'
import UAParser from 'ua-parser-js'
import { is_development } from '@utilities/commons'

const default_global_state = JSON.parse(vuex_global_state_config_object)
const default_module_state = JSON.parse(vuex_module_state_config_object)


const UNSET_SERVICE_TIMEOUT = -1
const DEFAULT_SERVICE_TIMEOUT = 60000
/**
 * Timeout for service handler calls expressed in milliseconds
 * If UNSET_SERVICE_TIMEOUT, the API will not reject using code, it will go to timeout naturally
 */
const default_services_timeout = {
  book: UNSET_SERVICE_TIMEOUT, // Used to be 180000 now unset to leave the timeout handling to the backend
}

const { device /*, browser, cpu*/ } = UAParser(navigator.userAgent)
const endpoint_config = process.env.VUE_APP_ENDPOINT + process.env.VUE_APP_CONFIG_API
const controller = new AbortController() // CancelToken deprecati => https://axios-http.com/docs/cancellation
const signal = controller.signal

// Map per tracciare le richieste attive per endpoint (array di controllers)
const active_requests = new Map()
const api = Axios.create({
  headers: {
    post: {},
    get: {},
  },
  withCredentials: process.env.VUE_APP_AXIOS_WITH_CREDENTIALS === "true"
})

let agent = {
  mobile: device.type == 'mobile' || device.type == 'tablet'  // [console, mobile, tablet, smarttv, wearable, embedded]
},
  endpoints = {},
  sennaBetaHost = {}, // Mappa degli endpoint Senna beta per dual-call
  sennaBetaEnabled = false, // Flag per abilitare dual-call verso beta
  axios_config = {
    headers: {
      post: {
        "Content-Type": "application/json",
      },
      get: {
        "Content-Type": "application/json",
      },
    },
    withCredentials: process.env.VUE_APP_AXIOS_WITH_CREDENTIALS === "true"
  } // => popolato dopo la get_config (Bearer ext_token)

signal.onabort = (event) => {
  log('[request aborted at service_handler]')
  log(event)
}


function set_axios_payload(payload = {}, service_config) {

  // if not external endpoints (eg: here.com) => add tokens/agent
  if (!service_config.external) {
    payload.token = Store.state.token
    payload.extToken = Store.state.ext_token
    payload.agent = agent
  }

  // [get] case (eg: here.com)
  if (service_config.api_method == 'get') return payload

  // [post] case (default) 
  return JSON.stringify(payload)
}

function service_promise_handler(promise, params = { caller: '[services/common.js] service_promise_handler()', success: (r) => { (r) }, error: (e) => { (e) } }) {
  // service_promise_handler gestisce una copia (passata per valore) della promise di axios per permettere di gestire l'errore a questo livello ed anche a livello di componente
  // la promise originale, SENZA error handler, viene ritornata al componente: in questo modo il componente può fare la sua gestione dell'errore e della response
  // la gestione di error/response delle due promises (una gestita a questo livello, una ritornata al componente) sono parallele, completamente indipendenti e opzionali: possono essere implementate entrambe, una sola delle due, nessuna
  const timeout = params.timeout || default_services_timeout[params.caller] || DEFAULT_SERVICE_TIMEOUT
  let service_handler_response_flag = false
  
  const promises = [promise]
  if (timeout > 0) {
    promises.push(
      new Promise((_, reject) =>
        setTimeout(() => {
          if (service_handler_response_flag) return

          service_handler('abort')
          reject(new Error('service handler timeout reached: ' + timeout))

          // reset common store
          Object.keys(default_global_state).forEach(k => {
            if (k == 'token') return
            if (k == 'ext_token') return
            let value = default_global_state[k]
            if (k == 'loading') value = false
            if (k == 'searching') value = false
            log(`[global store reset]: ${k} - ${value}`)
            Store.commit(k, value)
          })

          // reset modules store
          Object.keys(Store._modules.root._children).forEach(m => {
            Object.keys(default_module_state).forEach(k => {
              log(`[${m} module store reset]: ${k} - ${default_module_state[k]}`)
              Store.commit(`${m}/${k}`, default_module_state[k])
            })
          })
        }, timeout)
      )
    )
  }

  Promise
    .race(promises)
    .then(
      success => {
        service_handler_response_flag = true
        params.success(success)
      },
      error => {
        log(params.caller + ' [api error]', error)
        service_handler_response_flag = true
        params.error(error)
      },
    )
    .catch(error => {
      log(params.caller + ' [runtime error]', error)
      params.error(error)
    })
}

function send_notification(error) {
  let callback = function () {
    return log('Nessuna azione prevista')
  }

  let data = (error.response && error.response.data) ? error.response.data : ''
  if (data == '' && error.data) data = error.data

  if (data == '') {
    // errore non gestito lato server (es. PHP FATAL ERROR)
    data = {}
    data.routerPush = 'homepage'
  }

  if (error.response && error.response.status) data.status = error.response.status
  data.callback = callback
  window.eventBus.$emit('got_modal_notification', data)
  return error.response
}

// unica richiesta non centralizzata
export function get_config(token) {
  let params = { token: token, agent: agent }
  return api.post(
    endpoint_config,
    params,
  )
    .then((response) => {
      endpoints = response.data.endpoints

      const sennaPattern = /senna\.vadobay\.com/

      // Helper per iterare ricorsivamente sugli endpoint Senna
      const processSennaEndpoints = (obj, callback, path = '') => {
        Object.keys(obj).forEach(key => {
          const value = obj[key]
          const currentPath = path ? `${path}.${key}` : key
          if (typeof value === 'string' && sennaPattern.test(value)) {
            callback(obj, key, value, currentPath)
          } else if (typeof value === 'object' && value !== null) {
            processSennaEndpoints(value, callback, currentPath)
          }
        })
      }

      // 1. Override Senna endpoints se definito VUE_APP_SENNA_LOCAL (per development locale)
      if (is_development() && process.env.VUE_APP_SENNA_LOCAL) {
        const localBase = process.env.VUE_APP_SENNA_LOCAL.replace(/\/$/, '') // rimuove trailing slash
        processSennaEndpoints(endpoints, (obj, key, value, currentPath) => {
          const originalUrl = new URL(value)
          obj[key] = localBase + originalUrl.pathname
          log(`[Senna Local] Override ${currentPath}:`, obj[key])
        })
      }

      // 2. Setup Senna Beta endpoints per dual-call (per test in produzione)
      const betaEndpoint = response.data.sennaBetaHost
      if (betaEndpoint && typeof betaEndpoint === 'string' && betaEndpoint.trim() !== '') {
        const betaBase = betaEndpoint.replace(/\/$/, '') // rimuove trailing slash
        sennaBetaEnabled = true
        sennaBetaHost = {} // Reset

        processSennaEndpoints(endpoints, (obj, key, value, currentPath) => {
          // Usa l'endpoint originale (prima dell'override locale) per costruire il beta
          const originalUrl = new URL(value)
          const betaUrl = betaBase + originalUrl.pathname
          _.set(sennaBetaHost, currentPath, betaUrl)
          log(`[Senna Beta] Mapped ${currentPath}:`, betaUrl)
        })

        log('[Senna Beta] Dual-call enabled')
      } else {
        sennaBetaEnabled = false
        sennaBetaHost = {}
      }

      return response.data
    }, (error) => {
      log(error)
    })
}

// servizio centralizzato per ogni richiesta
export function service_handler(
  service_name,
  service_body = {},
  service_config = {}
) {
  service_config = {
    // default config
    ...{
      modal: true,
      external: false, // external call (eg. here) => hide tokens
      api_method: 'post',
      cancel_previous: false,
    },
    // requested config
    ...service_config
  }

  // interrompe tutte le pending request
  if (service_name == 'abort') {
    controller.abort()
    return
  }

  // crea un nuovo AbortController per questa richiesta specifica
  const request_controller = new AbortController()
  const request_signal = request_controller.signal

  // aggiunge il controller all'array delle richieste attive per questo endpoint
  if (!active_requests.has(service_name)) {
    active_requests.set(service_name, [])
  }
  active_requests.get(service_name).push(request_controller)

  // cancella tutte le richieste precedenti per lo stesso endpoint (se abilitato)
  if (service_config.cancel_previous) {
    const controllers = active_requests.get(service_name)
    const controllers_to_be_canceled = controllers.slice(0, -1)
    if (controllers_to_be_canceled.length > 0) {
      controllers_to_be_canceled.forEach(controller => {
        controller.abort()
      })
      log(`[request cancelled] ${controllers_to_be_canceled.length} previous request(s) for ${service_name} cancelled`)
      active_requests.set(service_name, [request_controller])
    }
  }

  const request_axios_config = {
    ...axios_config,
    signal: request_signal
  }

  const endpoint_url = _.get(endpoints, service_name)

  let promise = api[service_config.api_method](
    endpoint_url,
    set_axios_payload(service_body, service_config),
    request_axios_config,
  )

  // Dual-call verso Senna Beta (fire-and-forget, silenzioso)
  const betaEndpointUrl = _.get(sennaBetaHost, service_name)
  if (sennaBetaEnabled && betaEndpointUrl) {
    // Chiamata parallela al beta, completamente indipendente
    api[service_config.api_method](
      betaEndpointUrl,
      set_axios_payload(service_body, service_config),
      { ...axios_config } // config separata senza abort signal
    ).catch(() => {
      // Errori silenziosi - visibili solo nel Network tab
    })
    log(`[Senna Beta] Dual-call to: ${betaEndpointUrl}`)
  }

  const cleanup_request = () => {
    if (active_requests.has(service_name)) {
      const controllers = active_requests.get(service_name)
      const index = controllers.indexOf(request_controller)
      if (index > -1) {
        controllers.splice(index, 1)
        if (controllers.length === 0) {
          active_requests.delete(service_name)
        }
      }
    }
  }

  const promise_parameters = {
    caller: service_name,
    success: (response) => {
      // rimuove il controller dalla mappa
      cleanup_request()
      response
      // nessuna azione prevista
    },
    error: (error) => {
      // rimuove il controller dalla mappa
      cleanup_request()

      if (service_config.cancel_previous) return

      // trigger modal di errore (se non specificato diversamente da service_config, es: autocomplete)
      if (service_config.modal) send_notification(error)
      // es: if(service_name == 'otp') => gestisci una toast invece che una modal
    }
  }
  // This allows specif calls to explicit a different timeout rather than the default one set
  if (Number(service_config.timeout)) {
    promise_parameters.timeout = service_config.timeout
  }
  service_promise_handler(promise, promise_parameters)

  // cleanup anche per il promise restituito al componente
  promise
    .then(() => {
      cleanup_request()
    })
    .catch(() => {
      cleanup_request()
    })

  // gestione delegata al componente success/error
  return promise
}

export const is_service_present = (service_name) => !!_.get(endpoints, service_name)

export default {
}